import { FC, useRef } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  message,
  Select,
  Tooltip,
  Upload,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useModalConfirm, { Props, ReceivedProps } from "./hook";
import MessageItem, { IMessageItem } from "./MessageItem";
import {
  StyledChat,
  PriorityLabelRed,
  PriorityLabelOrange,
  PriorityLabelYellow,
  PriorityLabelGreen,
  UserName,
  FileTag,
  RemoveFile,
  SubmitButton,
  TooltipFileList,
  TooltipFileTag,
  EllipsisBadge,
} from "./styled";
import { ALLOWED_FILE_TYPES, ALLOWED_MIME_TYPES } from "../ModalChat/hook";
import EllipsisText from "./EllipsisText";
import moment from "moment";
import { renderStatusTag } from "./StatusTag";
import ResponseModeControl from "./ResponseModeControl";
import AiStatusIndicator from "./AiStatusIndicator";

const optionsPriority = [
  {
    label: <PriorityLabelRed>最</PriorityLabelRed>,
    value: 1,
  },
  {
    label: <PriorityLabelOrange>高</PriorityLabelOrange>,
    value: 2,
  },
  {
    label: <PriorityLabelYellow>中</PriorityLabelYellow>,
    value: 3,
  },
  {
    label: <PriorityLabelGreen>低</PriorityLabelGreen>,
    value: 4,
  },
];

const optionsInquiryType = [
  { label: "操作確認", value: "operation_confirmation" },
  { label: "不具合", value: "bug" },
  { label: "改善要望", value: "improvement" },
  { label: "相談", value: "consultation" },
  { label: "その他", value: "other" },
];

const ChatRealTimeLayout: FC<Props> = ({
  isAdmin,
  handleBack,
  userId,
  handleSubmit,
  form,
  uploadedFiles,
  setUploadedFiles,
  messageText,
  setMessageText,
  isLoading,
  chatContentRef,
  handleDeleteMessage,
  statusOptions,
  categoryOptions,
  handleRemoveFile,
  superAdminOptions,
  renderedMessages,
  threadDetail,
  handleUpdateChatThread,
  handleResponseModeChange,
  responseMode,
  isUpdatingResponseMode,
  aiRuns,
  isFullscreen,
  isLoadingMore,
  hasMoreMessages,
}) => {
  const overLimitRef = useRef(false);

  return (
    <ConfigProvider autoInsertSpaceInButton={false}>
      <StyledChat
        $isFullscreen={isFullscreen}
        $hasFiles={uploadedFiles?.length > 0}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="horizontal"
          onValuesChange={handleUpdateChatThread}
        >
          <div className="chat-header">
            {isAdmin ? (
              <div className="chat-header-admin">
                <button
                  type="button"
                  className="create-chat__header__back"
                  onClick={handleBack}
                >
                  <ArrowBackIcon className="icon-back" />
                  <span className="create-chat__header__back-label">戻る</span>
                </button>
                <div className="chat-header-admin__title-wrapper">
                  <EllipsisText
                    title={`#${threadDetail?.id || ""} ${
                      threadDetail?.title || ""
                    }`}
                    className="chat-header-admin__title"
                  />
                </div>
                <div className="chat-header-admin__info">
                  <img src="/images/iconUser.svg" alt="icon-user" />
                  <p>
                    <UserName>{threadDetail?.created_by || "-"}</UserName>
                    {threadDetail?.company_name || "-"}
                  </p>
                </div>
                <div className="chat-header-admin__row">
                  <Form.Item
                    label="ステータス"
                    name="status_id"
                    className="form-item-custom"
                  >
                    <Select options={statusOptions} className="select-custom" />
                  </Form.Item>
                  <Form.Item
                    label="担当者"
                    name="assigned_to_id"
                    className="form-item-custom form-item-custom__label"
                  >
                    <Select
                      options={superAdminOptions}
                      className="select-custom"
                    />
                  </Form.Item>
                </div>
                <div className="chat-header-admin__row">
                  <Form.Item
                    label="カテゴリー"
                    name="category_id"
                    className="form-item-custom"
                  >
                    <Select
                      options={categoryOptions}
                      className="select-custom"
                    />
                  </Form.Item>
                  <Form.Item
                    label="問い合わせ種別"
                    name="inquiry_type"
                    className="form-item-custom form-item-custom__label"
                  >
                    <Select
                      options={optionsInquiryType}
                      className="select-custom"
                      placeholder="未分類"
                    />
                  </Form.Item>
                </div>
                <div className="chat-header-admin__row">
                  <Form.Item
                    label="優先度"
                    name="priority"
                    className="form-item-custom"
                  >
                    <Select
                      options={optionsPriority}
                      className="select-custom"
                    />
                  </Form.Item>
                  <ResponseModeControl
                    isAdmin={!!isAdmin}
                    value={responseMode}
                    onChange={handleResponseModeChange}
                    loading={isUpdatingResponseMode}
                  />
                </div>
                <div className="chat-header-admin__row chat-header-admin-timer">
                  <div className="chat-time">
                    作成:{" "}
                    {moment(threadDetail?.created_at).format(
                      "YYYY/MM/DD HH:mm",
                    )}
                  </div>
                  <div className="chat-time-line"></div>
                  <div className="chat-time">
                    更新:{" "}
                    {moment(threadDetail?.updated_at).format(
                      "YYYY/MM/DD HH:mm",
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="chat-header__noti">
                  <p>#{threadDetail?.id || "-"}</p>
                  <div className="chat-header__title-wrapper">
                    <EllipsisText
                      title={threadDetail?.title || "-"}
                      className="chat-header__noti-title"
                    />
                  </div>
                </div>
                <div className="chat-header__info">
                  <img src="/images/iconUser.svg" alt="icon-user" />
                  <p>
                    <UserName>{threadDetail?.created_by || "-"}</UserName>
                    {threadDetail?.company_name || "-"}
                  </p>
                  <p></p>
                  <p>
                    {moment(threadDetail?.created_at).format(
                      "YYYY/MM/DD HH:mm",
                    )}
                  </p>
                </div>
                <div className="chat-header__status">
                  {renderStatusTag(threadDetail?.status_id, statusOptions)}
                </div>
              </>
            )}
          </div>
          <div
            className={isAdmin ? "chat-content-admin" : "chat-content"}
            ref={chatContentRef}
          >
            {isLoadingMore && (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px",
                  color: "#999",
                  fontSize: "12px",
                }}
              >
                読み込み中...
              </div>
            )}
            {renderedMessages
              ?.filter((mess) => mess && mess.id)
              ?.map((messageItem: IMessageItem) => (
                <MessageItem
                  key={messageItem.id}
                  data={messageItem}
                  userId={userId}
                  handleDeleteMessage={handleDeleteMessage}
                />
              ))}
            <AiStatusIndicator tracker={aiRuns} />
          </div>
          <div className="chat-footer">
            <div className="chat-footer__form">
              <Upload
                fileList={uploadedFiles}
                accept={ALLOWED_FILE_TYPES.join(",")}
                listType="picture"
                // maxCount={5}
                multiple
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");
                  const isVideo = file.type.startsWith("video/");
                  const isAllowed =
                    isImage ||
                    isVideo ||
                    ALLOWED_MIME_TYPES.includes(file.type);

                  if (!isAllowed) {
                    message.error(
                      "画像、動画、PDF、Word、Excel ファイルのみアップロードできます",
                    );
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onPreview={async (file) => {
                  let src = file.url as string;
                  if (!src && file.originFileObj) {
                    src = URL.createObjectURL(file.originFileObj);
                  }
                  if (file.type?.startsWith("image/")) {
                    const w = window.open(src);
                    w?.document.write(`<img src="${src}" style="width:100%"/>`);
                    return;
                  }
                  window.open(src, "_blank");
                }}
                onChange={({ fileList }) => {
                  let total = 0;
                  const validFiles = [];
                  let overLimit = false;
                  for (const file of fileList) {
                    const size =
                      file.size ||
                      (file.originFileObj ? file.originFileObj.size : 0);
                    if ((total + size) / 1024 / 1024 > 10) {
                      overLimit = true;
                      break;
                    }
                    total += size;
                    validFiles.push(file);
                  }
                  if (overLimit && !overLimitRef.current) {
                    message.error("総ファイルサイズは10MBまでの制限があります");
                    overLimitRef.current = true;
                    setTimeout(() => {
                      overLimitRef.current = false;
                    }, 500);
                  }
                  setUploadedFiles(validFiles);
                }}
                showUploadList={false}
              >
                <Button
                  className="btn-upload"
                  icon={<AttachFileIcon className="attach-icon" />}
                ></Button>
              </Upload>
              <div className="form-message">
                <TextArea
                  rows={uploadedFiles?.length > 0 ? 2 : 3}
                  placeholder="テキストを入力してください"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onBlur={(e) => setMessageText(e.target.value.trim())}
                  className="inp-message"
                />
                {uploadedFiles?.length > 0 && (
                  <div className="file-list">
                    {uploadedFiles.slice(0, 3).map((file) => (
                      <FileTag key={file.uid}>
                        <span className="file-name">{file.name}</span>
                        <RemoveFile onClick={() => handleRemoveFile(file.uid)}>
                          ×
                        </RemoveFile>
                      </FileTag>
                    ))}
                    {uploadedFiles.length > 3 && (
                      <Tooltip
                        color="#ffffff"
                        overlayInnerStyle={{
                          color: "#000000",
                          padding: "8px",
                          boxShadow:
                            "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                        }}
                        title={
                          <TooltipFileList>
                            {uploadedFiles.slice(3).map((file) => (
                              <TooltipFileTag key={file.uid}>
                                <span className="file-name">{file.name}</span>
                                <RemoveFile
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(file.uid);
                                  }}
                                >
                                  ×
                                </RemoveFile>
                              </TooltipFileTag>
                            ))}
                          </TooltipFileList>
                        }
                      >
                        <EllipsisBadge>...</EllipsisBadge>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
            <SubmitButton
              className="btn-submit"
              type="primary"
              htmlType="submit"
              loading={isLoading}
              disabled={
                messageText?.trim() === "" && uploadedFiles?.length === 0
              }
            >
              送信
            </SubmitButton>
          </div>
        </Form>
      </StyledChat>
    </ConfigProvider>
  );
};

const ChatRealTime: FC<ReceivedProps> = (props) => (
  <ChatRealTimeLayout {...useModalConfirm(props)} />
);

export default ChatRealTime;
